import { Gateway } from './types/gateway.types';

export type GatewayAccess = {
    isOwner: boolean;
    canViewTransactions: boolean;
    canViewWithdrawals: boolean;
    canViewLogs: boolean;
    canViewTransactionAnalytics: boolean;
    canViewCustomerBehaviorAnalytics: boolean;
    canExportRangeReport: boolean;
    canManageOverallSettings: boolean;
    canManageNotificationSettings: boolean;
    canEnterSettings: boolean;
    canEnterDashboard: boolean;
    canEnterGateway: boolean;
};

const permissionNamesForUser = (
    gateway: Gateway | undefined,
    profileId: number | undefined,
): string[] => {
    if (!gateway || !profileId) return [];

    return (
        gateway.permissions?.find((item) => item.user_id === profileId)
            ?.permissions ?? []
    );
};

export const getGatewayAccess = (
    gateway: Gateway | undefined,
    profileId: number | undefined,
): GatewayAccess => {
    const isOwner = Boolean(
        gateway && profileId && gateway.owner_id === profileId,
    );
    const permissions = permissionNamesForUser(gateway, profileId);

    const canViewTransactions =
        isOwner || permissions.includes('gateway-transaction-show');
    const canViewWithdrawals =
        isOwner ||
        permissions.includes('gateway-withdraw-list') ||
        permissions.includes('gateway-withdraw-show');
    const canViewLogs =
        isOwner || permissions.includes('gateway-log-inquiry-show');
    const canViewTransactionAnalytics =
        isOwner ||
        permissions.includes('gateway-transaction-analytics-show');
    const canViewCustomerBehaviorAnalytics =
        isOwner ||
        permissions.includes('gateway-customer-behavior-analytics-show');
    const canExportRangeReport =
        isOwner || permissions.includes('gateway-range-report-export');
    const canManageOverallSettings =
        isOwner || permissions.includes('gateway-settings-overall');
    const canManageNotificationSettings =
        isOwner || permissions.includes('gateway-settings-notifications');
    const canEnterSettings =
        canManageOverallSettings || canManageNotificationSettings;

    // The gateway dashboard is the common entry point. Members who only have
    // one of the settings permissions must still be able to enter it and use
    // Quick Access for the sections they were explicitly granted.
    const canEnterDashboard =
        isOwner || canViewTransactions || canViewWithdrawals || canEnterSettings;
    const canEnterGateway =
        canEnterDashboard ||
        canViewTransactionAnalytics ||
        canViewCustomerBehaviorAnalytics ||
        canExportRangeReport ||
        canEnterSettings;

    return {
        isOwner,
        canViewTransactions,
        canViewWithdrawals,
        canViewLogs,
        canViewTransactionAnalytics,
        canViewCustomerBehaviorAnalytics,
        canExportRangeReport,
        canManageOverallSettings,
        canManageNotificationSettings,
        canEnterSettings,
        canEnterDashboard,
        canEnterGateway,
    };
};

export const canAccessGatewaySettingsSection = (
    access: GatewayAccess,
    section?: string,
): boolean => {
    if (access.isOwner) return true;

    if (!section) return access.canEnterSettings;

    if (section === 'overall') return access.canManageOverallSettings;
    if (section === 'notifications') {
        return access.canManageNotificationSettings;
    }

    return false;
};
